import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDarkMode } from '../../../context/DarkModeContext';
import { useToast } from '../../../context/ToastContext';
import { IoCloseOutline, IoSaveOutline, IoTimeOutline } from 'react-icons/io5';

interface MetricData {
  id?: string;
  metric_name: string;
  current_value: number;
  baseline_value?: number;
  target_value?: number;
  metric_unit?: string;
  category?: string;
  notes?: string;
}

interface MetricsUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  metric?: MetricData;
  onSave: (metric: MetricData) => Promise<void>;
}

const METRIC_CATEGORIES = [
  { value: 'productivity', label: 'Productivity', icon: '📊' },
  { value: 'quality', label: 'Quality', icon: '✨' },
  { value: 'leadership', label: 'Leadership', icon: '👥' },
  { value: 'impact', label: 'Impact', icon: '🎯' },
  { value: 'collaboration', label: 'Collaboration', icon: '🤝' }
];

const COMMON_METRICS = [
  { name: 'Features Shipped', unit: 'features', category: 'productivity' },
  { name: 'Code Reviews Completed', unit: 'reviews', category: 'quality' },
  { name: 'Bugs Fixed', unit: 'bugs', category: 'quality' },
  { name: 'Team Members Mentored', unit: 'people', category: 'leadership' },
  { name: 'Documentation Pages', unit: 'pages', category: 'productivity' },
  { name: 'Customer Satisfaction', unit: '%', category: 'impact' },
  { name: 'Pull Requests Merged', unit: 'PRs', category: 'productivity' },
  { name: 'Technical Debt Reduced', unit: '%', category: 'quality' }
];

const MetricsUpdateModal: React.FC<MetricsUpdateModalProps> = ({
  isOpen,
  onClose,
  metric,
  onSave
}) => {
  const { darkMode } = useDarkMode();
  const { showToast } = useToast();
  const [formData, setFormData] = useState<MetricData>({
    metric_name: '',
    current_value: 0,
    baseline_value: undefined,
    target_value: undefined,
    metric_unit: '',
    category: 'productivity',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showMetricSuggestions, setShowMetricSuggestions] = useState(false);

  useEffect(() => {
    if (metric) {
      setFormData(metric);
    } else {
      setFormData({
        metric_name: '',
        current_value: 0,
        baseline_value: undefined,
        target_value: undefined,
        metric_unit: '',
        category: 'productivity',
        notes: ''
      });
    }
  }, [metric]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.metric_name.trim()) {
      showToast('Please enter a metric name', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      // Ensure current_value is a number before saving
      const dataToSave = {
        ...formData,
        current_value: formData.current_value || 0
      };
      await onSave(dataToSave);
      showToast(metric ? 'Metric updated successfully' : 'Metric created successfully', 'success');
      onClose();
    } catch (error) {
      console.error('Error saving metric:', error);
      showToast('Failed to save metric', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectCommonMetric = (commonMetric: typeof COMMON_METRICS[0]) => {
    setFormData(prev => ({
      ...prev,
      metric_name: commonMetric.name,
      metric_unit: commonMetric.unit,
      category: commonMetric.category
    }));
    setShowMetricSuggestions(false);
  };

  // Style classes
  const bgColor = darkMode ? 'bg-gray-900' : 'bg-white';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const inputBg = darkMode ? 'bg-gray-800' : 'bg-gray-50';

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div className="absolute inset-0 bg-black/50" />

        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className={`relative w-full max-w-lg ${bgColor} rounded-xl shadow-2xl`}
          onClick={e => e.stopPropagation()}
        >
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className={`flex items-center justify-between p-6 border-b ${borderColor}`}>
              <h2 className={`text-xl font-semibold ${textColor}`}>
                {metric ? 'Update Metric' : 'Add New Metric'}
              </h2>
              <button
                type="button"
                onClick={onClose}
                className={`p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${mutedTextColor}`}
              >
                <IoCloseOutline className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Metric Name */}
              <div>
                <label className={`block text-sm font-medium ${textColor} mb-1`}>
                  Metric Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.metric_name}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, metric_name: e.target.value }));
                      // Keep suggestions open if input is empty, close if user starts typing
                      if (e.target.value.trim()) {
                        setShowMetricSuggestions(false);
                      }
                    }}
                    onFocus={() => !metric && setShowMetricSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowMetricSuggestions(false), 200)} // Delay to allow click on suggestion
                    className={`w-full px-3 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="Type your own metric or select below"
                  />
                  
                  {/* Metric Suggestions */}
                  {showMetricSuggestions && !metric && (
                    <div className={`absolute z-10 w-full mt-1 ${bgColor} border ${borderColor} rounded-lg shadow-lg max-h-60 overflow-y-auto`}>
                      <p className={`px-3 py-2 text-xs ${mutedTextColor} border-b ${borderColor}`}>
                        Select a suggestion or type your own metric
                      </p>
                      {COMMON_METRICS.map((m) => (
                        <button
                          key={m.name}
                          type="button"
                          onClick={() => selectCommonMetric(m)}
                          className={`w-full px-3 py-2 text-left ${textColor} hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm">{m.name}</span>
                            <span className={`text-xs ${mutedTextColor}`}>{m.unit}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Category */}
              <div>
                <label className={`block text-sm font-medium ${textColor} mb-1`}>
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className={`w-full px-3 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                >
                  {METRIC_CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>
                      {cat.icon} {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Values Grid */}
              <div className="grid grid-cols-3 gap-4">
                {/* Current Value */}
                <div>
                  <label className={`block text-sm font-medium ${textColor} mb-1`}>
                    Current Value*
                  </label>
                  <input
                    type="number"
                    value={formData.current_value === 0 ? '' : formData.current_value}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData(prev => ({ 
                        ...prev, 
                        current_value: value === '' ? 0 : parseFloat(value) || 0 
                      }));
                    }}
                    className={`w-full px-3 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="0"
                    required
                    step="any"
                  />
                </div>

                {/* Baseline Value */}
                <div>
                  <label className={`block text-sm font-medium ${textColor} mb-1`}>
                    Baseline
                  </label>
                  <input
                    type="number"
                    value={formData.baseline_value || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      baseline_value: e.target.value ? parseFloat(e.target.value) : undefined 
                    }))}
                    className={`w-full px-3 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="Optional"
                  />
                </div>

                {/* Target Value */}
                <div>
                  <label className={`block text-sm font-medium ${textColor} mb-1`}>
                    Target
                  </label>
                  <input
                    type="number"
                    value={formData.target_value || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      target_value: e.target.value ? parseFloat(e.target.value) : undefined 
                    }))}
                    className={`w-full px-3 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="Optional"
                  />
                </div>
              </div>

              {/* Unit */}
              <div>
                <label className={`block text-sm font-medium ${textColor} mb-1`}>
                  Unit (optional)
                </label>
                <input
                  type="text"
                  value={formData.metric_unit || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, metric_unit: e.target.value }))}
                  className={`w-full px-3 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="e.g., features, %, hours"
                />
              </div>

              {/* Notes */}
              <div>
                <label className={`block text-sm font-medium ${textColor} mb-1`}>
                  Notes (optional)
                </label>
                <textarea
                  value={formData.notes || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className={`w-full px-3 py-2 rounded-lg border ${borderColor} ${inputBg} ${textColor} resize-none focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  placeholder="Any additional context or details..."
                />
              </div>
            </div>

            {/* Footer */}
            <div className={`flex items-center justify-between p-6 border-t ${borderColor}`}>
              <div className={`flex items-center gap-2 text-sm ${mutedTextColor}`}>
                <IoTimeOutline className="w-4 h-4" />
                <span>Updates track your progress over time</span>
              </div>
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className={`px-4 py-2 rounded-lg font-medium ${darkMode ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} transition-colors`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
                    isSubmitting
                      ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                      : 'bg-blue-500 hover:bg-blue-600 text-white'
                  } transition-colors`}
                >
                  <IoSaveOutline className="w-5 h-5" />
                  {isSubmitting ? 'Saving...' : (metric ? 'Update' : 'Add Metric')}
                </button>
              </div>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MetricsUpdateModal;